import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";

interface RecentBooking {
  compraId: number;
  status: string;
  pacoteNome: string;
  motoristaNome: string;
  dataReserva: string;
  horaReserva: string;
  precoTotal: number;
  pacote?: {
    id: number;
    titulo: string;
    precoBase: number;
    duracaoEmMinutos: number;
    primeiraFotoId?: string | number;
  };
}

const RecentBookingsCard: React.FC = () => {
    const [bookings, setBookings] = useState<RecentBooking[]>([]);
    const [filter, setFilter] = useState<string>("all");

    useEffect(() => {
        const fetchBookings = async () => {
            try {
                const token = localStorage.getItem("token");
                const res = await axios.get(
                    `${process.env.REACT_APP_API_URL}/usuarios/dashboard/recent-bookings`,
                    {
                        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
                    }
                );
                setBookings(res.data);
            } catch (error) {
                console.error("Erro ao buscar reservas recentes:", error);
            }
        };

        fetchBookings();
    }, []);

    const filteredBookings =
        filter === "all"
            ? bookings
            : bookings.filter((b) => b.status.toLowerCase() === filter.toLowerCase());

    const getStatusBadge = (status: string) => {
        switch (status) {
            case "CONCLUIDO":
                return <span className="badge badge-success badge-sm rounded-pill">Completed</span>;
            case "PAGO":
                return <span className="badge badge-info badge-sm rounded-pill">Paid</span>;
            case "EM_ANDAMENTO":
                return <span className="badge badge-warning badge-sm rounded-pill">In Progress</span>;
            case "PENDENTE":
                return <span className="badge badge-soft-secondary badge-sm rounded-pill">Pending</span>;
            case "CANCELADO":
                return <span className="badge badge-danger badge-sm rounded-pill">Cancelled</span>;
            default:
                return null;
        }
    };

    return (
        <div className="col-xl-7 d-flex">
            <div className="card shadow-none flex-fill">
                <div className="card-body">
                    {/* Header */}
                    <div className="d-flex justify-content-between align-items-center mb-4">
                        <h6>Recent Bookings</h6>

                        <div className="dropdown">
                            <Link
                                to="#"
                                className="dropdown-toggle btn bg-light-200 btn-sm text-gray-6 rounded-pill fw-normal fs-14 d-inline-flex align-items-center"
                                data-bs-toggle="dropdown"
                            >
                                <i className="isax isax-document-filter me-2 fs-14 text-gray-6" />
                                Filter
                            </Link>
                            <ul className="dropdown-menu dropdown-menu-end p-3">
                                <li>
                                    <button
                                        onClick={() => setFilter("all")}
                                        className="dropdown-item rounded-1"
                                    >
                                        <i className="ti ti-point-filled me-1" />
                                        All
                                    </button>
                                </li>
                                <li>
                                    <button
                                        onClick={() => setFilter("CONCLUIDO")}
                                        className="dropdown-item rounded-1"
                                    >
                                        <i className="ti ti-point-filled me-1" />
                                        Completed
                                    </button>
                                </li>
                                <li>
                                    <button
                                        onClick={() => setFilter("EM_ANDAMENTO")}
                                        className="dropdown-item rounded-1"
                                    >
                                        <i className="ti ti-point-filled me-1" />
                                        In Progress
                                    </button>
                                </li>
                                <li>
                                    <button
                                        onClick={() => setFilter("PAGO")}
                                        className="dropdown-item rounded-1"
                                    >
                                        <i className="ti ti-point-filled me-1" />
                                        Paid
                                    </button>
                                </li>
                            </ul>
                        </div>
                    </div>

                    {/* Lista de reservas */}
                    {filteredBookings.length === 0 ? (
                        <p className="text-muted mb-0">Nenhuma reserva encontrada.</p>
                    ) : (
                        filteredBookings.map((booking) => (
                            <div key={booking.compraId} className="card shadow-none mb-3 border">
                                <div className="card-body">
                                    <div className="d-flex align-items-center flex-wrap row-gap-2">
                                        {/* Imagem do pacote */}
                                        <Link
                                            to={`/flight-details/${booking.pacote ? booking.pacote.id : "#"}`}
                                            className="avatar avatar-xl flex-shrink-0 me-2"
                                        >
                                            <img
                                                src={
                                                    booking.pacote?.primeiraFotoId
                                                        ? `${process.env.REACT_APP_API_URL}/uploads/${booking.pacote.primeiraFotoId}`
                                                        : "/assets/img/tours/default.jpg"
                                                }
                                                className="img-fluid rounded-circle"
                                                alt={booking.pacoteNome || "Tour"}
                                                onError={(e) => {
                                                    (e.target as HTMLImageElement).src = "/assets/img/tours/default.jpg";
                                                }}
                                            />
                                        </Link>

                                        <div className="flex-fill">
                                            <span className="badge badge-soft-pink badge-xs rounded-pill mb-1">
                                                <i className="isax isax-signpost me-1" />
                                                Tour
                                            </span>

                                            <div className="d-flex justify-content-between align-items-center mb-1">
                                                <h6 className="fs-16 text-truncate mb-0">
                                                    <Link
                                                        to={`/flight-details/${booking.pacote ? booking.pacote.id : "#"}`}
                                                    >
                                                        {booking.pacoteNome}
                                                    </Link>
                                                </h6>
                                                {getStatusBadge(booking.status)}
                                            </div>

                                            <div className="d-flex align-items-center flex-wrap">
                                                <p className="fs-14 d-flex align-items-center border-end pe-2 me-2 mb-0">
                                                    <i className="isax isax-calendar-25 me-2" />
                                                    Date:{" "}
                                                    {new Date(booking.dataReserva).toLocaleDateString()}
                                                </p>
                                                <p className="fs-14 d-flex align-items-center mb-0">
                                                    <i className="isax isax-timer5 me-2" />
                                                    Time: {booking.horaReserva?.slice(0, 5)}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>
        </div>
    );
};

export default RecentBookingsCard;
